import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    FormControlLabel,
    Button,
    Card,
    CardContent,
    IconButton,
    InputAdornment,
    FormControl,
    RadioGroup,
    Radio,
    Divider,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { Input } from 'components';
import { CONTACT_NUMBER_FIELD_MAX_LENGTH, FIFTY_FIELD_MAX_LENGTH, MAX_CREDIT_REQUIRED_LENGTH, TEXT_FIELD_MAX_LENGTH } from './validationLimits';

const EMAIL_LENGTH_ERROR = `Email Address cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const CONTACT_NAME_LENGTH_ERROR = `Contact Name cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const POSITION_LENGTH_ERROR = `Position cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const PHONE_NUMBER_LENGTH_ERROR = 'Phone number cannot exceed 10 digits.';
const COMPANY_WEBSITE_LENGTH_ERROR = `Company Website cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const PARENT_COMPANY_LENGTH_ERROR = `Parent Company cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const PREVIOUS_BUSINESS_NAMES_LENGTH_ERROR = `Previous Business Names cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const MAX_CREDIT_REQUIRED_LENGTH_ERROR = `Maximum Credit Required cannot exceed ${MAX_CREDIT_REQUIRED_LENGTH} characters.`;
const DIRECTOR_NAME_LENGTH_ERROR = `Director Name cannot exceed ${TEXT_FIELD_MAX_LENGTH} characters.`;
const CONTACT_NAME_FIELD_MAX_LENGTH_ERROR = `Contact Name cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const COMPANY_FIELD_MAX_LENGTH_ERROR = `Company cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;
const EMAIL_FIELD_MAX_LENGTH_ERROR = `Email Address cannot exceed ${FIFTY_FIELD_MAX_LENGTH} characters.`;

interface CreditReferee {
    contactName: string;
    company: string;
    phoneNumber: string;
    email: string;
    id?: string;
}

interface CreditAccountData {
    copyContactDetails: boolean;
    contactName: string;
    email: string;
    position: string;
    phoneNumber: string;
    companyWebsite: string;
    natureOfBusiness: string;
    dateOfIncorporation: string;
    parentCompany: string;
    previousBusiness: string;

    hasCreditReferees: string;
    creditReferees: CreditReferee[];

    maxCreditRequired: string;

    isTrustee: string;
    directorName: string;
}

interface FormData {
    creditAccountData?: CreditAccountData;
    companyName?: string;
    contactName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    invoiceStatement?: {
        contactName?: string;
        emailAddressInvoices?: string;
        phoneNumber?: string;
    };
    companyBusinessName?: string;
    emailAddressStatements?: string;
}

interface Props {
    formData: FormData;
    onFormDataChange: (data: Partial<FormData>) => void;
    onNext: () => void;
    onBack: () => void;
    onSave?: () => void;
    isSaving?: boolean;
    isDisabled?: boolean;
    externalErrors?: Record<string, string>;
}

const getDefaultCreditAccountData = (): CreditAccountData => ({
    copyContactDetails: false,
    contactName: '',
    email: '',
    position: '',
    phoneNumber: '',
    companyWebsite: '',
    natureOfBusiness: '',
    dateOfIncorporation: '',
    parentCompany: '',
    previousBusiness: '',
    hasCreditReferees: '',
    creditReferees: [
        { contactName: '', company: '', phoneNumber: '', email: '' }
    ],
    maxCreditRequired: '0',
    isTrustee: '',
    directorName: ''
});

export const CreditAccountDetails: React.FC<Props> = ({
    formData,
    onFormDataChange,
    onNext,
    onBack,
    onSave,
    isSaving,
    isDisabled,
    externalErrors = {}
}) => {
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [clearedExternalErrors, setClearedExternalErrors] = useState<Set<string>>(new Set());

    // Merge errors but exclude external errors that have been cleared by user input
    const allErrors = {
        ...Object.fromEntries(
            Object.entries(externalErrors).filter(([key]) => !clearedExternalErrors.has(key))
        ),
        ...errors
    };
    const creditData = formData.creditAccountData || getDefaultCreditAccountData();

    const ensureCreditReferees = (data: CreditAccountData): CreditAccountData => {
        return {
            ...data,
            creditReferees: data.creditReferees && Array.isArray(data.creditReferees) && data.creditReferees.length > 0
                ? data.creditReferees
                : []
        };
    };

    useEffect(() => {
        if (Object.keys(externalErrors).length > 0) {
            setErrors(externalErrors);
            // Reset cleared external errors when new external errors come in
            setClearedExternalErrors(new Set());
        }
    }, [externalErrors]);

    const safeCreditData = ensureCreditReferees(creditData);

    const handleInputChange = (field: string, value: any) => {
        const updatedCreditData = {
            ...safeCreditData,
            [field]: value
        };

        const updatedFormData = {
            ...formData,
            creditAccountData: updatedCreditData
        };

        onFormDataChange({
            creditAccountData: updatedCreditData
        });

        const validationErrors =
            validateCreditAccountDetails(updatedFormData);

        setErrors(prev => ({
            ...prev,
            [field]: validationErrors[field]
        }));

        if (!validationErrors[field] && externalErrors[field]) {
            setClearedExternalErrors(prev => {
                const updated = new Set(prev);
                updated.add(field);
                return updated;
            });
        }
    };

    const handleProvideCreditRefereesChange = (value: string) => {
        const updatedCreditData = {
            ...safeCreditData,
            hasCreditReferees: value
        };

        if (value === 'no') {
            updatedCreditData.creditReferees = [];
        } else if (value === 'yes') {
            // Always reset to just 1 referee when selecting yes
            updatedCreditData.creditReferees = [
                { contactName: '', company: '', phoneNumber: '', email: '' }
            ];
        }

        onFormDataChange({
            creditAccountData: updatedCreditData
        });

        if (errors.hasCreditReferees) {
            setErrors(prev => ({ ...prev, hasCreditReferees: undefined }));
        }

        // Mark external error as cleared
        if (externalErrors.hasCreditReferees) {
            setClearedExternalErrors(prev => new Set(prev).add('hasCreditReferees'));
        }
    };

    const handleRefereeChange = (
        id: string | undefined,
        index: number,
        field: keyof CreditReferee,
        value: string
    ) => {
        const updatedReferees = safeCreditData.creditReferees.map(
            (referee, currentIndex) => {
                const isMatchingSavedReferee =
                    Boolean(id) && referee.id === id;

                const isMatchingNewReferee =
                    !id &&
                    !referee.id &&
                    currentIndex === index;

                if (
                    !isMatchingSavedReferee &&
                    !isMatchingNewReferee
                ) {
                    return referee;
                }

                return {
                    ...referee,
                    [field]: value
                };
            }
        );

        const updatedCreditData = {
            ...safeCreditData,
            creditReferees: updatedReferees
        };

        const updatedFormData = {
            ...formData,
            creditAccountData: updatedCreditData
        };

        onFormDataChange({
            creditAccountData: updatedCreditData
        });

        const validationErrors =
            validateCreditAccountDetails(updatedFormData);

        const refereeKey = id || index;
        const errorKey = `referee_${refereeKey}_${field}`;

        setErrors(prev => ({
            ...prev,
            [errorKey]: validationErrors[errorKey]
        }));

        if (!validationErrors[errorKey] && externalErrors[errorKey]) {
            setClearedExternalErrors(prev => {
                const updated = new Set(prev);
                updated.add(errorKey);
                return updated;
            });
        }
    };

    const addCreditReferee = () => {
        if (creditData.creditReferees.length >= 2) return;
        const newReferee: CreditReferee = {
            contactName: '',
            company: '',
            phoneNumber: '',
            email: ''
        };
        const updatedReferees = [...safeCreditData.creditReferees, newReferee];
        handleInputChange('creditReferees', updatedReferees);
    };

    const removeCreditReferee = (id: string | undefined, index: number) => {
        if (safeCreditData.creditReferees.length > 1) {
            const updatedReferees = safeCreditData.creditReferees.filter((referee, currentIndex) => {
                if (id) {
                    return referee.id !== id;
                }

                return currentIndex !== index;
            });
            handleInputChange('creditReferees', updatedReferees);

            const newErrors = { ...errors };
            Object.keys(newErrors).forEach(key => {
                const refereeKey = id || index;
                if (key.includes(`referee_${refereeKey}_`)) {
                    delete newErrors[key];
                }
            });
            setErrors(newErrors);
        }
    };

    const validateCreditAccountDetails = (data = formData) => {
        const newErrors: Record<string, string> = {};

        if (!data.creditAccountData.contactName?.trim()) {
            newErrors.contactName = 'Contact Name is required';
        } else if (
            data.creditAccountData.contactName.trim().length >
            FIFTY_FIELD_MAX_LENGTH
        ) {
            newErrors.contactName = CONTACT_NAME_LENGTH_ERROR;
        }

        if (!data.creditAccountData.email?.trim()) {
            newErrors.email = 'Email Address is required';
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.creditAccountData.email)
        ) {
            newErrors.email = 'Email Address is not valid';
        } else if (
            data.creditAccountData.email.trim().length >
            FIFTY_FIELD_MAX_LENGTH
        ) {
            newErrors.email = EMAIL_LENGTH_ERROR;
        }

        if (!data.creditAccountData.position?.trim()) {
            newErrors.position = 'Position is required';
        } else if (
            data.creditAccountData.position.trim().length >
            FIFTY_FIELD_MAX_LENGTH
        ) {
            newErrors.position = POSITION_LENGTH_ERROR;
        }

        if (!data.creditAccountData.phoneNumber?.trim()) {
            newErrors.phoneNumber = 'Phone Number is required';
        } else if (
            data.creditAccountData.phoneNumber.trim().length >
            CONTACT_NUMBER_FIELD_MAX_LENGTH
        ) {
            newErrors.phoneNumber = PHONE_NUMBER_LENGTH_ERROR;
        }

        if (!data.creditAccountData.companyWebsite?.trim()) {
            newErrors.companyWebsite = 'Company Website is required';
        } else if (
            data.creditAccountData.companyWebsite.trim().length >
            TEXT_FIELD_MAX_LENGTH
        ) {
            newErrors.companyWebsite = COMPANY_WEBSITE_LENGTH_ERROR;
        }

        if (data.creditAccountData.parentCompany?.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.parentCompany = PARENT_COMPANY_LENGTH_ERROR;
        }

        if (data.creditAccountData.previousBusiness?.trim().length > TEXT_FIELD_MAX_LENGTH) {
            newErrors.previousBusiness = PREVIOUS_BUSINESS_NAMES_LENGTH_ERROR;
        }

        if (
            data.creditAccountData.maxCreditRequired?.length >
            MAX_CREDIT_REQUIRED_LENGTH
        ) {
            newErrors.maxCreditRequired = MAX_CREDIT_REQUIRED_LENGTH_ERROR;
        }

        if (!data.creditAccountData.natureOfBusiness?.trim()) {
            newErrors.natureOfBusiness = 'Nature of Business is required';
        }

        if (!data.creditAccountData.dateOfIncorporation?.trim()) {
            newErrors.dateOfIncorporation =
                'Date of Incorporation is required';
        }

        if (!data.creditAccountData.isTrustee) {
            newErrors.isTrustee =
                'Please select if applicant is a trustee';
        }

        if (!data.creditAccountData.directorName?.trim()) {
            newErrors.directorName = 'Name of Director/s is required';
        } else if (
            data.creditAccountData.directorName.trim().length >
            TEXT_FIELD_MAX_LENGTH
        ) {
            newErrors.directorName = DIRECTOR_NAME_LENGTH_ERROR;
        }

        if (!data.creditAccountData.hasCreditReferees) {
            newErrors.hasCreditReferees = 'Please select an option';
        }

        // Credit Referees Validation
        if (
            data.creditAccountData.hasCreditReferees === 'yes' &&
            data.creditAccountData.creditReferees &&
            Array.isArray(data.creditAccountData.creditReferees)
        ) {
            data.creditAccountData.creditReferees.forEach(
                (referee: any, index: number) => {
                    const refereeKey = referee.id ?? index;

                    if (!referee.contactName?.trim()) {
                        newErrors[`referee_${refereeKey}_contactName`] =
                            `Referee ${index + 1} Contact Name is required`;
                    } else if (
                        referee.contactName.trim().length >
                        FIFTY_FIELD_MAX_LENGTH
                    ) {
                        newErrors[`referee_${refereeKey}_contactName`] =
                            `Referee ${index + 1} ${CONTACT_NAME_FIELD_MAX_LENGTH_ERROR}`;
                    }

                    if (!referee.company?.trim()) {
                        newErrors[`referee_${refereeKey}_company`] =
                            `Referee ${index + 1} Company is required`;
                    } else if (
                        referee.company.trim().length >
                        FIFTY_FIELD_MAX_LENGTH
                    ) {
                        newErrors[`referee_${refereeKey}_company`] =
                            `Referee ${index + 1} ${COMPANY_FIELD_MAX_LENGTH_ERROR}`;
                    }

                    if (!referee.phoneNumber?.trim()) {
                        newErrors[`referee_${refereeKey}_phoneNumber`] =
                            `Referee ${index + 1} Phone Number is required`;
                    } else if (
                        referee.phoneNumber.trim().length >
                        CONTACT_NUMBER_FIELD_MAX_LENGTH
                    ) {
                        newErrors[`referee_${refereeKey}_phoneNumber`] =
                            `Referee ${index + 1} ${PHONE_NUMBER_LENGTH_ERROR}`;
                    }

                    if (!referee.email?.trim()) {
                        newErrors[`referee_${refereeKey}_email`] =
                            `Referee ${index + 1} Email is required`;
                    } else if (
                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                            referee.email
                        )
                    ) {
                        newErrors[`referee_${refereeKey}_email`] =
                            `Referee ${index + 1} Email is not valid`;
                    } else if (
                        referee.email.trim().length >
                        FIFTY_FIELD_MAX_LENGTH
                    ) {
                        newErrors[`referee_${refereeKey}_email`] =
                            `Referee ${index + 1} ${EMAIL_FIELD_MAX_LENGTH_ERROR}`;
                    }
                }
            );
        }

        return newErrors;
    };

    const handleNext = () => {
        const validationErrors = validateCreditAccountDetails();

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        onNext();
    };

    const toTitleCase = (str = '') =>
        str
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
                Credit Account Details
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Input
                        label="Contact Name"
                        value={safeCreditData.contactName}
                        onChange={(e) => handleInputChange('contactName', e.target.value)}
                        onBlur={(e) => handleInputChange('contactName', toTitleCase(e.target.value))}
                        fullWidth
                        required
                        error={!!allErrors.contactName}
                        helperText={allErrors.contactName}
                        data-testid="contact-name-input"
                        size='small'
                        InputLabelProps={{ shrink: !!safeCreditData.contactName }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Email Address"
                        type="email"
                        value={safeCreditData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        fullWidth
                        required
                        error={!!allErrors.email}
                        helperText={allErrors.email}
                        data-testid="email-address-input"
                        size='small'
                        InputLabelProps={{ shrink: !!safeCreditData.email }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Position"
                        value={safeCreditData.position}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                        onBlur={(e) => handleInputChange('position', toTitleCase(e.target.value))}
                        fullWidth
                        required
                        error={!!allErrors.position}
                        helperText={allErrors.position}
                        data-testid="position-input"
                        size='small'
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Phone Number"
                        value={safeCreditData.phoneNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[0-9]+$/.test(value)) {
                                handleInputChange('phoneNumber', value);
                            }
                        }}
                        fullWidth
                        required
                        error={!!allErrors.phoneNumber}
                        helperText={allErrors.phoneNumber}
                        data-testid="phone-number-input"
                        size='small'
                        InputLabelProps={{ shrink: !!safeCreditData.phoneNumber }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Company Website"
                        value={safeCreditData.companyWebsite}
                        onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
                        fullWidth
                        required
                        error={!!allErrors.companyWebsite}
                        helperText={allErrors.companyWebsite}
                        data-testid="company-website-input"
                        size='small'
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small" required error={!!allErrors.natureOfBusiness}>
                        <InputLabel sx={{ fontSize: '12px' }}>Nature of Business</InputLabel>
                        <Select
                            value={safeCreditData.natureOfBusiness}
                            label="Nature of Business"
                            onChange={(e) => handleInputChange('natureOfBusiness', e.target.value)}
                            data-testid="nature-of-business-select"
                            sx={{ fontSize: '12px' }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 200,
                                        overflowY: 'auto'
                                    }
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left'
                                }
                            }}
                        >
                            <MenuItem value="Consultant – Environmental/Engineering">Consultant – Environmental/Engineering</MenuItem>
                            <MenuItem value="Consultant – OHS">Consultant – OHS</MenuItem>
                            <MenuItem value="Consultant – Other">Consultant – Other</MenuItem>
                            <MenuItem value="Government/Council">Government/Council</MenuItem>
                            <MenuItem value="Industry – Trade Waste">Industry – Trade Waste</MenuItem>
                            <MenuItem value="Industry – Water Board">Industry – Water Board</MenuItem>
                            <MenuItem value="Industry – Other">Industry – Other</MenuItem>
                            <MenuItem value="Private">Private</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                        {allErrors.natureOfBusiness && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {allErrors.natureOfBusiness}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Date of Incorporation"
                        type="date"
                        value={safeCreditData.dateOfIncorporation}
                        onChange={(e) => handleInputChange('dateOfIncorporation', e.target.value)}
                        fullWidth
                        required
                        error={!!allErrors.dateOfIncorporation}
                        helperText={allErrors.dateOfIncorporation}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        data-testid="date-of-incorporation-input"
                        size='small'
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Input
                        label="Parent Company (if any)"
                        value={safeCreditData.parentCompany}
                        onChange={(e) => handleInputChange('parentCompany', e.target.value)}
                        fullWidth
                        data-testid="parent-company-input"
                        size='small'
                    />
                </Grid>

                <Grid item xs={12}>
                    <Input
                        label="Previous Business Name/s (if any)"
                        value={safeCreditData.previousBusiness}
                        onChange={(e) => handleInputChange('previousBusiness', e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        data-testid="previous-business-names-input"
                        size='small'
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={7}>
                    <Input
                        label="Maximum Credit Required"
                        value={safeCreditData.maxCreditRequired}
                        onChange={(e) => handleInputChange('maxCreditRequired', e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        data-testid="maximum-credit-input"
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} md={5}>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Note: Amounts payable within 30 days from the invoice date
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                    <Typography variant="body1" sx={{ fontWeight: 500, py: 1 }}>
                        Is the applicant a trustee for any trust? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                    </Typography>
                    {errors.isTrustee && (
                        <Typography variant="caption" color="error.main">
                            {errors.isTrustee}
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" error={!!errors.isTrustee}>
                        <RadioGroup
                            value={safeCreditData.isTrustee}
                            onChange={(e) => handleInputChange('isTrustee', e.target.value)}
                            row
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio size="small" />}
                                label="Yes"
                                data-testid="trustee-yes"
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio size="small" />}
                                label="No"
                                data-testid="trustee-no"
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Input
                        label="Name of Director/s or Proprietors"
                        value={safeCreditData.directorName}
                        onChange={(e) => handleInputChange('directorName', e.target.value)}
                        onBlur={(e) => handleInputChange('directorName', toTitleCase(e.target.value))}
                        fullWidth
                        required
                        error={!!allErrors.directorName}
                        helperText={allErrors.directorName}
                        multiline
                        rows={3}
                        data-testid="directors-name-input"
                        size='small'
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main', mb: 2 }}>
                    Credit Referees
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body1" sx={{ fontWeight: 500, py: 1 }}>
                            Would you wish to provide credit referees? <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                        </Typography>
                        {errors.hasCreditReferees && (
                            <Typography variant="caption" color="error.main">
                                {errors.hasCreditReferees}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl component="fieldset" error={!!errors.hasCreditReferees}>
                            <RadioGroup
                                value={safeCreditData.hasCreditReferees || ''}
                                onChange={(e) => handleProvideCreditRefereesChange(e.target.value)}
                                row
                                sx={{ justifyContent: 'flex-start' }}
                            >
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                    data-testid="provide-referees-yes"
                                />
                                <FormControlLabel
                                    value="no"
                                    control={<Radio size="small" />}
                                    label="No"
                                    data-testid="provide-referees-no"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>

                {safeCreditData.hasCreditReferees === 'yes' && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addCreditReferee}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                                data-testid="add-referee-button"
                                disabled={safeCreditData.creditReferees?.length >= 2}
                            >
                                Add Referee
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            {safeCreditData.creditReferees && Array.isArray(safeCreditData.creditReferees) && safeCreditData.creditReferees.map((referee, index) => (
                                <Grid item xs={12} key={referee.id || `referee-${index}`}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            backgroundColor: '#f8f9fa',
                                            border: '2px solid #e3f2fd'
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    Credit Referee {index + 1}
                                                    <Typography component="span" color="error.main" sx={{ ml: 1, fontSize: '0.875rem' }}>
                                                        (Required)
                                                    </Typography>
                                                </Typography>
                                                {index >= 1 && (
                                                    <IconButton
                                                        onClick={() => removeCreditReferee(referee.id, index)}
                                                        color="error"
                                                        size="small"
                                                        data-testid={`remove-referee-${referee.id || index}`}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Box>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Input
                                                        label="Contact Name"
                                                        value={referee.contactName}
                                                        onChange={(e) => handleRefereeChange(referee.id, index, 'contactName', e.target.value)}
                                                        onBlur={(e) => handleRefereeChange(referee.id, index, 'contactName', toTitleCase(e.target.value))}
                                                        required
                                                        fullWidth
                                                        size="small"
                                                        error={!!allErrors[`referee_${referee.id ?? index}_contactName`]}
                                                        helperText={allErrors[`referee_${referee.id ?? index}_contactName`]}
                                                        data-testid={`referee-${referee.id ?? index}-contact-name`}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Input
                                                        label="Company"
                                                        value={referee.company}
                                                        onChange={(e) => handleRefereeChange(referee.id, index, 'company', e.target.value)}
                                                        required
                                                        fullWidth
                                                        size="small"
                                                        error={!!allErrors[`referee_${referee.id ?? index}_company`]}
                                                        helperText={allErrors[`referee_${referee.id ?? index}_company`]}
                                                        data-testid={`referee-${referee.id ?? index}-company`}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Input
                                                        label="Phone Number"
                                                        value={referee.phoneNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === '' || /^[0-9]+$/.test(value)) {
                                                                handleRefereeChange(referee.id, index, 'phoneNumber', value);
                                                            }
                                                        }}
                                                        required
                                                        fullWidth
                                                        size="small"
                                                        error={!!allErrors[`referee_${referee.id ?? index}_phoneNumber`]}
                                                        helperText={allErrors[`referee_${referee.id ?? index}_phoneNumber`]}
                                                        data-testid={`referee-${referee.id ?? index}-phone-number`}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <Input
                                                        label="Email Address"
                                                        type="email"
                                                        value={referee.email}
                                                        onChange={(e) => handleRefereeChange(referee.id, index, 'email', e.target.value)}
                                                        required
                                                        fullWidth
                                                        size="small"
                                                        error={!!allErrors[`referee_${referee.id ?? index}_email`]}
                                                        helperText={allErrors[`referee_${referee.id ?? index}_email`]}
                                                        data-testid={`referee-${referee.id ?? index}-email-address`}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 4,
                    flexDirection: {
                        xs: 'column',
                        sm: 'row',
                    },
                    gap: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onBack}
                    size="medium"
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        width: {
                            xs: '100%',
                            sm: 'auto',
                        },
                    }}
                    data-testid="back-button"
                >
                    Back
                </Button>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: {
                            xs: 'column',
                            sm: 'row',
                        },
                        width: {
                            xs: '100%',
                            sm: 'auto',
                        },
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onSave}
                        disabled={isSaving || isDisabled}
                        size="medium"
                        startIcon={<SaveIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto',
                            },
                        }}
                        data-testid="save-button"
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleNext}
                        size="medium"
                        disabled={isDisabled}
                        startIcon={<RateReviewIcon />}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'none',
                            width: {
                                xs: '100%',
                                sm: 'auto',
                            },
                        }}
                        data-testid="next-step-button"
                    >
                        Review &amp; Confirmation
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};